import { Controller } from "@hotwired/stimulus"

// Renders the "Clusters" view on the Violations page as a force-directed threat
// topology. Three node tiers: human OWNERS -> their AGENTS -> the violation
// CATEGORIES they trigger. Ownership edges (owner-agent) are dashed; activity
// edges (actor-category) are weighted by violation count and colored by actor.
// Category hubs are sized by volume and ringed by their dominant severity.
// Hover focuses a node's neighborhood; drag rearranges; click drills into the
// filtered list. A ranked leaderboard sits below. Data comes from
// ViolationClusterBuilder via the `payload` value.
const NS = "http://www.w3.org/2000/svg"
const LEGEND_MAX = 16
const PALETTE = [
  "#60a5fa", "#a78bfa", "#34d399", "#fbbf24", "#f472b6", "#22d3ee",
  "#fb7185", "#a3e635", "#c084fc", "#38bdf8", "#f59e0b", "#4ade80"
]
const OWNER_COLOR = "#818cf8"
const SEV = {
  critical: { rank: 4, color: "#f87171", label: "Critical", weight: 8 },
  high: { rank: 3, color: "#fb923c", label: "High", weight: 4 },
  medium: { rank: 2, color: "#facc15", label: "Medium", weight: 2 },
  low: { rank: 1, color: "#60a5fa", label: "Low", weight: 1 }
}
const SEV_ORDER = ["critical", "high", "medium", "low"]

export default class extends Controller {
  static targets = ["root"]
  static values = { payload: Object, basePath: String, filters: Object }

  connect() {
    this.render()
    this.themeObserver = new MutationObserver(() => this.render())
    this.themeObserver.observe(document.documentElement, {
      attributes: true, attributeFilter: ["data-theme"]
    })
  }

  disconnect() {
    if (this.themeObserver) this.themeObserver.disconnect()
  }

  /* ------------------------------------------------------------------ */
  /* Data                                                                */
  /* ------------------------------------------------------------------ */

  actors() {
    return (this.payloadValue.nodes || []).map((n) => ({
      id: n.actor_id,
      pid: n.actor_public_id,
      label: (n.actor_label || "").trim(),
      isAgent: !!n.is_agent,
      ownerId: n.owner_id,
      ownerLabel: (n.owner_label || "").trim(),
      ownerPid: n.owner_public_id,
      counts: n.counts || {},
      severity: n.severity || {},
      open: n.open || 0,
      blocked: n.blocked || 0,
      total: Object.values(n.counts || {}).reduce((a, v) => a + v, 0)
    })).sort((a, b) => b.total - a.total)
  }

  categories(actors) {
    const labels = this.payloadValue.category_labels || {}
    const totals = {}
    for (const n of actors) for (const [k, v] of Object.entries(n.counts)) totals[k] = (totals[k] || 0) + v
    return Object.keys(totals)
      .filter((k) => totals[k] > 0)
      .sort((a, b) => totals[b] - totals[a])
      .map((k) => ({ key: k, label: labels[k] || k, volume: totals[k] }))
  }

  span(actor) {
    return Object.values(actor.counts).filter((v) => v > 0).length
  }

  contributors(catKey, actors) {
    return actors
      .filter((n) => (n.counts[catKey] || 0) > 0)
      .map((n) => ({ id: n.id, pid: n.pid, label: n.label, count: n.counts[catKey] }))
      .sort((a, b) => b.count - a.count)
  }

  dominantSeverity(catKey) {
    const mix = (this.payloadValue.category_severity || {})[catKey] || {}
    let best = null
    for (const [sev, n] of Object.entries(mix)) {
      if (!n) continue
      const meta = SEV[sev] || SEV.low
      if (!best || n > best.n || (n === best.n && meta.rank > best.rank)) best = { sev, n, rank: meta.rank }
    }
    return best ? (SEV[best.sev] || SEV.low) : SEV.low
  }

  /* ------------------------------------------------------------------ */
  /* Theme                                                               */
  /* ------------------------------------------------------------------ */

  readTheme() {
    const cs = getComputedStyle(document.documentElement)
    const val = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback)
    return {
      light: document.documentElement.getAttribute("data-theme") === "light",
      textPrimary: val("--text-primary", "#f1f5f9"),
      textSecondary: val("--text-secondary", "#94a3b8"),
      textMuted: val("--text-muted", "#64748b"),
      card: val("--bg-card", "#111827"),
      border: val("--border-color", "rgba(255,255,255,0.12)")
    }
  }

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  render() {
    const actors = this.actors()
    const cats = this.categories(actors)
    this.theme = this.readTheme()
    this.colorById = new Map()
    actors.forEach((a, i) => this.colorById.set(a.id, PALETTE[i % PALETTE.length]))

    const root = this.rootTarget
    root.innerHTML = ""
    root.className = "space-y-4"
    root.style.position = "relative"
    const add = (el) => { if (el) root.appendChild(el) }
    add(this.topologyCard(actors, cats))
    add(this.pieCard(actors, cats))
    add(this.findingsCard(actors, cats))
    add(this.postureCard(actors, cats))
    add(this.trendCard())
    add(this.heatmapCard())
    add(this.detectorCard(actors, cats))
    add(this.riskCard(actors, cats))
    root.appendChild(this.buildTooltip())
  }

  /* ------------------------------------------------------------------ */
  /* Aggregations (enforcement, trend, momentum, risk)                   */
  /* ------------------------------------------------------------------ */

  enforcement() {
    return this.payloadValue.enforcement || { blocked: 0, flagged: 0, open: 0, resolved: 0, total: 0 }
  }

  trend() {
    return this.payloadValue.trend || []
  }

  // Momentum from the trend: second half vs first half of the window.
  momentum() {
    const tr = this.trend()
    if (tr.length < 4) return null
    const mid = Math.floor(tr.length / 2)
    const first = tr.slice(0, mid).reduce((a, b) => a + (b.total || 0), 0)
    const second = tr.slice(mid).reduce((a, b) => a + (b.total || 0), 0)
    if (first === 0 && second === 0) return null
    const pct = first === 0 ? 100 : Math.round(((second - first) / first) * 100)
    return { pct, up: second >= first }
  }

  // Severity-weighted risk score for an actor.
  actorRisk(actor) {
    const sev = actor.severity || {}
    return SEV_ORDER.reduce((a, s) => a + (sev[s] || 0) * SEV[s].weight, 0)
  }

  /* ------------------------------------------------------------------ */
  /* Key findings (auto narrative)                                       */
  /* ------------------------------------------------------------------ */

  findingsCard(actors, cats) {
    const findings = this.computeFindings(actors, cats)
    if (!findings.length) return null
    const card = h("div", "bg-slate-800/30 border border-slate-700 rounded-xl p-4 lg:p-5")
    card.appendChild(hText("h3", "text-sm font-semibold text-white tracking-tight mb-3", "Key findings"))
    const list = h("div", "space-y-2")
    findings.forEach((f) => {
      const row = h("div", "flex items-start gap-2.5")
      const dot = h("span", "mt-1.5 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0")
      dot.style.background = f.color || "#60a5fa"
      const txt = h("p", "text-[13px] text-gray-300 leading-snug")
      txt.innerHTML = f.html
      row.appendChild(dot); row.appendChild(txt)
      list.appendChild(row)
    })
    card.appendChild(list)
    return card
  }

  computeFindings(actors, cats) {
    const out = []
    const enf = this.enforcement()
    const total = enf.total || cats.reduce((a, c) => a + c.volume, 0)
    if (!total) return out
    const strong = (s) => `<span style="color:var(--text-primary,#fff);font-weight:600;">${esc(s)}</span>`

    // Momentum.
    const mo = this.momentum()
    if (mo && Math.abs(mo.pct) >= 15) {
      out.push({
        color: mo.up ? SEV.critical.color : "#34d399",
        html: `Violations are ${mo.up ? "trending up" : "trending down"} ${strong(Math.abs(mo.pct) + "%")} across this period.`
      })
    }

    // Concentration: fewest actors covering >=80%.
    const ranked = [...actors].sort((a, b) => b.total - a.total)
    let acc = 0, k = 0
    for (const a of ranked) { acc += a.total; k++; if (acc / total >= 0.8) break }
    if (ranked.length > 1 && k <= Math.max(1, Math.ceil(ranked.length * 0.4))) {
      out.push({
        color: "#a78bfa",
        html: `${strong(k + (k === 1 ? " identity" : " identities"))} ${k === 1 ? "accounts" : "account"} for ${strong(Math.round((acc / total) * 100) + "%")} of all violations.`
      })
    }

    // Monoculture: a category dominated by a single actor.
    for (const cat of cats) {
      const cont = this.contributors(cat.key, actors)
      if (cont.length && cont[0].count / cat.volume >= 0.9 && cat.volume >= 3) {
        const share = Math.round((cont[0].count / cat.volume) * 100)
        out.push({
          color: this.colorById.get(cont[0].id),
          html: `${strong(cat.label)} is ${strong(share + "%")} driven by ${strong(cont[0].label)}.`
        })
        break
      }
    }

    // Severity hotspot: where do criticals live?
    const critByCat = cats.map((c) => ({ c, n: ((this.payloadValue.category_severity || {})[c.key] || {}).critical || 0 }))
      .filter((x) => x.n > 0).sort((a, b) => b.n - a.n)
    if (critByCat.length) {
      out.push({
        color: SEV.critical.color,
        html: `Most critical-severity activity is in ${strong(critByCat[0].c.label)} (${strong(critByCat[0].n.toString())} critical).`
      })
    }

    // Enforcement gap.
    if (total >= 5) {
      const flaggedPct = Math.round((enf.flagged / total) * 100)
      if (flaggedPct >= 40) {
        out.push({
          color: SEV.high.color,
          html: `${strong(flaggedPct + "%")} of violations were ${strong("flagged but not blocked")} — review-only, not enforced.`
        })
      }
    }

    // Open backlog.
    if (total >= 5) {
      const openPct = Math.round((enf.open / total) * 100)
      if (openPct >= 50) {
        out.push({
          color: "#fbbf24",
          html: `${strong(openPct + "%")} of violations remain ${strong("unresolved")} (${enf.open.toLocaleString()} open).`
        })
      }
    }

    return out.slice(0, 5)
  }

  /* ------------------------------------------------------------------ */
  /* Risk posture (severity / enforcement / resolution bars)             */
  /* ------------------------------------------------------------------ */

  postureCard(actors, cats) {
    const enf = this.enforcement()
    const total = enf.total || 0
    if (!total) return null
    const sevT = this.payloadValue.severity_totals || {}

    const card = h("div", "bg-slate-800/30 border border-slate-700 rounded-xl p-4 lg:p-5")
    const header = h("div", "mb-3")
    header.appendChild(hText("h3", "text-sm font-semibold text-white tracking-tight", "Risk posture"))
    header.appendChild(hText("p", "text-xs text-gray-500 mt-0.5",
      `${total.toLocaleString()} violations \u00b7 severity, enforcement & source mix \u00b7 click a band to drill`))
    card.appendChild(header)

    const bars = h("div", "space-y-3.5")
    bars.appendChild(this.postureBar("Severity", total, SEV_ORDER.map((s) => (
      { label: SEV[s].label, value: sevT[s] || 0, color: SEV[s].color, drill: { severity: s } }
    ))))
    bars.appendChild(this.postureBar("Enforcement", total, [
      { label: "Blocked", value: enf.blocked || 0, color: "#34d399", drill: { status: "blocked" } },
      { label: "Flagged only", value: enf.flagged || 0, color: "#60a5fa" }
    ]))
    const agentVol = actors.filter((a) => a.isAgent).reduce((s, a) => s + a.total, 0)
    const userVol = actors.filter((a) => !a.isAgent).reduce((s, a) => s + a.total, 0)
    bars.appendChild(this.postureBar("Source", agentVol + userVol || total, [
      { label: "AI agents", value: agentVol, color: "#a78bfa" },
      { label: "Human users", value: userVol, color: "#2dd4bf" }
    ]))
    card.appendChild(bars)
    return card
  }

  postureBar(title, total, segs) {
    const wrap = h("div")
    const head = h("div", "flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5")
    head.appendChild(hText("span", "text-[11px] text-gray-500 uppercase tracking-wide w-24 shrink-0", title))
    const legend = h("div", "flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400")
    segs.filter((s) => s.value > 0).forEach((s) => {
      const i = h("span", "inline-flex items-center gap-1.5")
      const d = h("span", "inline-block w-2 h-2 rounded-sm"); d.style.background = s.color
      i.appendChild(d)
      i.appendChild(hText("span", "", `${s.label} ${s.value.toLocaleString()} (${Math.round((s.value / total) * 100)}%)`))
      legend.appendChild(i)
    })
    head.appendChild(legend)
    wrap.appendChild(head)

    const track = h("div", "relative w-full h-3 rounded-full overflow-hidden bg-slate-500/10 flex")
    segs.forEach((s) => {
      if (s.value <= 0) return
      const seg = h("div", "h-full")
      seg.style.width = `${(s.value / total) * 100}%`
      seg.style.background = s.color
      if (s.drill) {
        seg.style.cursor = "pointer"
        seg.addEventListener("click", () => { window.location.href = this.listUrl(s.drill) })
      }
      this.hoverHandlers(seg, () =>
        `<div style="font-weight:600;">${esc(s.label)}</div>` +
        `<div style="margin-top:2px;">${s.value.toLocaleString()} of ${total.toLocaleString()} \u00b7 ${Math.round((s.value / total) * 100)}%</div>`)
      track.appendChild(seg)
    })
    wrap.appendChild(track)
    return wrap
  }

  /* ------------------------------------------------------------------ */
  /* Activity over time (severity-banded columns)                        */
  /* ------------------------------------------------------------------ */

  trendCard() {
    const tr = this.trend()
    if (tr.length < 2) return null
    const t = this.theme

    const card = h("div", "bg-slate-800/30 border border-slate-700 rounded-xl p-4 lg:p-5")
    const header = h("div", "flex flex-wrap items-start justify-between gap-3 mb-3")
    const left = h("div")
    left.appendChild(hText("h3", "text-sm font-semibold text-white tracking-tight", "Activity over time"))
    left.appendChild(hText("p", "text-xs text-gray-500 mt-0.5",
      `By ${this.payloadValue.trend_bucket || "day"} \u00b7 stacked by severity \u00b7 hover for detail`))
    header.appendChild(left)
    const legend = h("div", "flex flex-wrap items-center gap-3 text-[11px] text-gray-400")
    SEV_ORDER.forEach((s) => {
      const i = h("span", "inline-flex items-center gap-1.5")
      const d = h("span", "inline-block w-2 h-2 rounded-sm"); d.style.background = SEV[s].color
      i.appendChild(d); i.appendChild(hText("span", "", SEV[s].label)); legend.appendChild(i)
    })
    header.appendChild(legend)
    card.appendChild(header)

    const W = 960, H = 188, padX = 6, padTop = 6, padBottom = 22
    const n = tr.length
    const max = Math.max(1, ...tr.map((d) => d.total || 0))
    const plotH = H - padTop - padBottom
    // Cap the per-bar slot so a handful of buckets cluster together with a tidy
    // gap (instead of stretching across the full width), then center the group.
    const step = Math.min(72, (W - padX * 2) / n)
    const barW = Math.max(2, Math.min(48, step * 0.68))
    const used = step * n
    const offset = padX + ((W - padX * 2) - used) / 2
    const colX = (i) => offset + i * step + (step - barW) / 2

    const svg = svgEl("svg", { viewBox: `0 0 ${W} ${H}`, width: "100%", role: "img" })
    svg.style.cssText = "display:block;width:100%;height:auto;overflow:visible;"
    svg.appendChild(svgEl("line", { x1: offset, y1: H - padBottom, x2: offset + used, y2: H - padBottom, stroke: t.border, "stroke-width": 1 }))

    const order = ["low", "medium", "high", "critical"]
    tr.forEach((d, i) => {
      let y = H - padBottom
      const x = colX(i)
      order.forEach((s) => {
        const v = d[s] || 0
        if (v <= 0) return
        const segH = (v / max) * plotH
        y -= segH
        svg.appendChild(svgEl("rect", { x, y, width: barW, height: segH, fill: SEV[s].color, rx: Math.min(2, barW / 2) }))
      })
      const ov = svgEl("rect", { x: offset + i * step, y: padTop, width: step, height: plotH, fill: "transparent" })
      ov.style.cursor = "default"
      this.hoverHandlers(ov, () => this.trendTooltip(d))
      svg.appendChild(ov)
    })

    const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) } catch (e) { return iso } }
    const ticks = n <= 7 ? tr.map((_, i) => i) : [0, Math.floor(n / 2), n - 1]
    ticks.forEach((i) => svg.appendChild(svgText(colX(i) + barW / 2, H - 6, t.textMuted, 10, fmt(tr[i].t), 500)))

    const box = h("div"); box.appendChild(svg); card.appendChild(box)
    return card
  }

  trendTooltip(d) {
    const t = this.theme
    let when = d.t
    try { when = new Date(d.t).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) } catch (e) { /* noop */ }
    const rows = SEV_ORDER.filter((s) => d[s]).map((s) =>
      `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:8px;">` +
      `<span style="width:8px;height:8px;border-radius:9999px;background:${SEV[s].color};"></span>${SEV[s].label} ${d[s]}</span>`).join("")
    return `<div style="font-weight:600;">${esc(when)}</div>` +
      `<div style="color:${t.textMuted};font-size:11px;">${d.total || 0} violation${(d.total || 0) === 1 ? "" : "s"}</div>` +
      (rows ? `<div style="margin-top:5px;display:flex;flex-wrap:wrap;">${rows}</div>` : "")
  }

  /* ------------------------------------------------------------------ */
  /* When it happens (hour x weekday heatmap)                            */
  /* ------------------------------------------------------------------ */

  heatmapCard() {
    const act = this.payloadValue.activity || []
    const crit = this.payloadValue.activity_critical || []
    if (!act.length) return null
    let max = 0, total = 0
    for (const row of act) for (const v of row) { max = Math.max(max, v); total += v }
    if (!total) return null

    const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    const card = h("div", "bg-slate-800/30 border border-slate-700 rounded-xl p-4 lg:p-5")
    const header = h("div", "mb-3")
    header.appendChild(hText("h3", "text-sm font-semibold text-white tracking-tight", "When it happens"))
    const tz = this.payloadValue.time_zone || "UTC"
    header.appendChild(hText("p", "text-xs text-gray-500 mt-0.5",
      `Violations by weekday and hour \u00b7 darker = more \u00b7 red outline = includes critical \u00b7 times in ${tz}`))
    card.appendChild(header)

    const grid = h("div")
    grid.style.cssText = "display:grid;grid-template-columns:34px repeat(24, minmax(0, 1fr));gap:2px;align-items:center;"

    grid.appendChild(h("div"))
    for (let hr = 0; hr < 24; hr++) {
      const lab = h("div", "text-[9px] text-gray-500 text-center")
      lab.textContent = hr % 3 === 0 ? String(hr) : ""
      grid.appendChild(lab)
    }

    for (let d = 0; d < 7; d++) {
      grid.appendChild(hText("div", "text-[10px] text-gray-500 pr-1 text-right", DOW[d]))
      for (let hr = 0; hr < 24; hr++) {
        const v = (act[d] && act[d][hr]) || 0
        const c = (crit[d] && crit[d][hr]) || 0
        const cell = h("div", "rounded-sm")
        cell.style.cssText = `height:16px;background:${this.heatColor(v, max)};`
        if (c > 0) cell.style.boxShadow = `inset 0 0 0 1.5px ${SEV.critical.color}`
        if (v > 0) {
          this.hoverHandlers(cell, () =>
            `<div style="font-weight:600;">${DOW[d]} \u00b7 ${this.hourLabel(hr)}</div>` +
            `<div style="color:${this.theme.textMuted};font-size:11px;">${v} violation${v === 1 ? "" : "s"}${c ? ` \u00b7 ${c} critical` : ""}</div>`)
        }
        grid.appendChild(cell)
      }
    }
    card.appendChild(grid)
    return card
  }

  heatColor(v, max) {
    if (v <= 0) return this.theme.light ? "rgba(15,23,42,0.05)" : "rgba(255,255,255,0.04)"
    const norm = Math.pow(v / max, 0.6)
    return `rgba(96,165,250,${(0.15 + norm * 0.85).toFixed(3)})`
  }

  hourLabel(hr) {
    const ampm = hr < 12 ? "am" : "pm"
    const h12 = hr % 12 === 0 ? 12 : hr % 12
    return `${h12}${ampm}`
  }

  /* ------------------------------------------------------------------ */
  /* Detector breakdown (per verifier / type volume + block rate)        */
  /* ------------------------------------------------------------------ */

  detectorCard(actors, cats) {
    if (!cats.length) return null
    const blockedMap = this.payloadValue.category_blocked || {}
    const rows = cats.slice(0, 8)
    const maxVol = Math.max(1, ...rows.map((c) => c.volume))

    const card = h("div", "bg-slate-800/30 border border-slate-700 rounded-xl p-4 lg:p-5")
    const header = h("div", "mb-3")
    header.appendChild(hText("h3", "text-sm font-semibold text-white tracking-tight", "Detector breakdown"))
    header.appendChild(hText("p", "text-xs text-gray-500 mt-0.5",
      "Volume and block rate by verifier / detection type \u00b7 click to drill"))
    card.appendChild(header)

    const list = h("div", "space-y-1")
    rows.forEach((c) => {
      const blocked = blockedMap[c.key] || 0
      const rate = c.volume ? Math.round((blocked / c.volume) * 100) : 0
      const sev = this.dominantSeverity(c.key)

      const row = h("div", "group cursor-pointer rounded-lg px-2 py-2 -mx-2 transition-colors hover:bg-slate-500/5 flex items-center gap-3")
      row.addEventListener("click", () => { window.location.href = this.listUrl(this.catLink(c.key)) })

      const mid = h("div", "min-w-0 flex-1")
      const name = h("span", "block text-[13px] font-medium text-gray-200 group-hover:text-white truncate")
      name.textContent = c.label; name.title = c.label
      mid.appendChild(name)
      const track = h("div", "relative w-full h-2 rounded-full overflow-hidden bg-slate-500/10 mt-1.5")
      const fill = h("div", "absolute inset-y-0 left-0 rounded-full")
      fill.style.width = `${Math.max(3, (c.volume / maxVol) * 100)}%`
      fill.style.background = sev.color
      track.appendChild(fill); mid.appendChild(track)
      row.appendChild(mid)

      const stats = h("div", "flex items-center gap-3 flex-shrink-0 text-right")
      stats.appendChild(this.statCell(c.volume.toLocaleString(), "total"))
      stats.appendChild(this.statCell(`${rate}%`, "blocked", rate >= 50 ? "#34d399" : SEV.high.color))
      row.appendChild(stats)
      list.appendChild(row)
    })
    card.appendChild(list)
    return card
  }

  statCell(val, label, color) {
    const w = h("div", "leading-tight")
    const v = h("div", "text-[13px] font-semibold tabular-nums")
    v.style.color = color || "var(--text-primary,#fff)"
    v.textContent = val
    w.appendChild(v)
    w.appendChild(hText("div", "text-[10px] text-gray-500 uppercase tracking-wide", label))
    return w
  }

  /* ------------------------------------------------------------------ */
  /* Agents to investigate (risk-weighted)                               */
  /* ------------------------------------------------------------------ */

  riskCard(actors, cats) {
    const ranked = actors
      .map((a) => ({ a, risk: this.actorRisk(a) }))
      .filter((x) => x.risk > 0)
      .sort((x, y) => y.risk - x.risk)
      .slice(0, 6)
    if (!ranked.length) return null

    const card = h("div", "bg-slate-800/30 border border-slate-700 rounded-xl p-4 lg:p-5")
    const header = h("div", "mb-3")
    header.appendChild(hText("h3", "text-sm font-semibold text-white tracking-tight", "Agents to investigate"))
    header.appendChild(hText("p", "text-xs text-gray-500 mt-0.5", "Ranked by severity-weighted risk, not raw volume \u00b7 click to drill"))
    card.appendChild(header)

    const maxRisk = Math.max(1, ...ranked.map((r) => r.risk))
    const list = h("div", "space-y-1")
    ranked.forEach((r, i) => list.appendChild(this.riskRow(r.a, r.risk, maxRisk, i + 1)))
    card.appendChild(list)
    return card
  }

  riskRow(actor, risk, maxRisk, rank) {
    const sev = actor.severity || {}
    const open = actor.open || 0
    const blocked = actor.blocked || 0
    const blockedPct = actor.total ? Math.round((blocked / actor.total) * 100) : 0

    const row = h("div", "group cursor-pointer rounded-lg px-2 py-2 -mx-2 transition-colors hover:bg-slate-500/5 flex items-center gap-3")
    if (actor.pid) row.addEventListener("click", () => { window.location.href = this.listUrl({ member_uuid: actor.pid }) })

    const num = h("span", "text-xs font-semibold text-gray-500 tabular-nums w-4 text-right flex-shrink-0")
    num.textContent = rank
    row.appendChild(num)

    const dot = h("span", "inline-block w-2.5 h-2.5 rounded-full flex-shrink-0")
    dot.style.background = this.colorById.get(actor.id)
    row.appendChild(dot)

    const mid = h("div", "min-w-0 flex-1")
    const top = h("div", "flex items-center gap-2")
    const name = h("span", "text-[13px] font-medium text-gray-200 group-hover:text-white truncate")
    name.textContent = actor.label
    name.title = actor.label
    top.appendChild(name)
    const roleBadge = h("span", "text-[10px] uppercase tracking-wide text-gray-500 flex-shrink-0")
    roleBadge.textContent = actor.isAgent ? "agent" : "user"
    top.appendChild(roleBadge)
    mid.appendChild(top)

    // Severity-weighted risk bar (stacked by severity).
    const track = h("div", "relative w-full h-2 rounded-full overflow-hidden bg-slate-500/10 mt-1.5")
    const fill = h("div", "absolute inset-y-0 left-0 flex rounded-full overflow-hidden")
    fill.style.width = `${Math.max(3, (risk / maxRisk) * 100)}%`
    SEV_ORDER.forEach((s) => {
      const wgt = (sev[s] || 0) * SEV[s].weight
      if (wgt <= 0) return
      const seg = h("div", "h-full")
      seg.style.width = `${(wgt / risk) * 100}%`
      seg.style.background = SEV[s].color
      this.hoverHandlers(seg, () => `<div style="font-weight:600;">${esc(actor.label)}</div><div style="margin-top:2px;">${sev[s]} ${SEV[s].label.toLowerCase()} violation${sev[s] === 1 ? "" : "s"}</div>`)
      fill.appendChild(seg)
    })
    track.appendChild(fill)
    mid.appendChild(track)
    row.appendChild(mid)

    const stats = h("div", "flex items-center gap-3 flex-shrink-0 text-right")
    const meta = (val, label, color) => {
      const w = h("div", "leading-tight")
      const v = h("div", "text-[13px] font-semibold tabular-nums")
      v.style.color = color || "var(--text-primary,#fff)"
      v.textContent = val
      w.appendChild(v)
      w.appendChild(hText("div", "text-[10px] text-gray-500 uppercase tracking-wide", label))
      return w
    }
    stats.appendChild(meta(actor.total.toLocaleString(), "total"))
    if ((sev.critical || 0) > 0) stats.appendChild(meta(sev.critical.toString(), "crit", SEV.critical.color))
    stats.appendChild(meta(open.toString(), "open", open > 0 ? "#fbbf24" : undefined))
    stats.appendChild(meta(`${blockedPct}%`, "blocked", blockedPct < 50 ? SEV.high.color : "#34d399"))
    row.appendChild(stats)

    return row
  }

  topologyCard(actors, cats) {
    const card = h("div", "bg-slate-800/30 border border-slate-700 rounded-xl p-4 lg:p-5")
    const header = h("div", "flex flex-wrap items-start justify-between gap-3 mb-1")
    const left = h("div")
    left.appendChild(hText("h3", "text-sm font-semibold text-white tracking-tight", "Violation topology"))
    const agentCount = actors.filter((a) => a.isAgent).length
    const ownerCount = new Set(actors.map((a) => a.ownerId)).size
    left.appendChild(hText("p", "text-xs text-gray-500 mt-0.5",
      `${cats.length} categories  \u00b7  ${agentCount} agents  \u00b7  ${ownerCount} owners  \u00b7  hover to trace, drag to arrange, click to drill`))
    header.appendChild(left)
    header.appendChild(this.nodeKeyEl())
    card.appendChild(header)
    card.appendChild(this.topologySvg(actors, cats))
    card.appendChild(this.legendEl(actors))
    return card
  }

  // Small key explaining the three node tiers.
  nodeKeyEl() {
    const wrap = h("div", "flex items-center gap-3 text-[11px] text-gray-400")
    const mk = (color, label, ring) => {
      const i = h("span", "flex items-center gap-1.5")
      const d = h("span", "inline-block w-2.5 h-2.5 rounded-full")
      d.style.background = ring ? "transparent" : color
      if (ring) d.style.boxShadow = `inset 0 0 0 2px ${color}`
      i.appendChild(d); i.appendChild(hText("span", "", label)); return i
    }
    wrap.appendChild(mk("#94a3b8", "Category", false))
    wrap.appendChild(mk("#60a5fa", "Agent", false))
    wrap.appendChild(mk(OWNER_COLOR, "Owner", true))
    return wrap
  }

  /* ---------- topology graph ---------- */

  buildGraph(actors, cats) {
    const nodes = []
    const index = new Map()
    const add = (key, n) => { n.idx = nodes.length; index.set(key, n.idx); nodes.push(n); return n.idx }

    const maxVol = Math.max(1, ...cats.map((c) => c.volume))
    cats.forEach((c) => {
      const sev = this.dominantSeverity(c.key)
      add(`c:${c.key}`, {
        type: "category", key: c.key, label: c.label, value: c.volume,
        r: 20 + Math.sqrt(c.volume / maxVol) * 22, color: sev.color, sev
      })
    })

    const actorIds = new Set(actors.map((a) => a.id))
    const maxTotal = Math.max(1, ...actors.map((a) => a.total))
    actors.forEach((a) => {
      add(`a:${a.id}`, {
        type: a.isAgent ? "agent" : "human", actorId: a.id, pid: a.pid, label: a.label,
        value: a.total, r: 7 + Math.sqrt(a.total / maxTotal) * 11,
        color: this.colorById.get(a.id), actor: a
      })
    })

    // Standalone owner nodes (owners with no direct violations of their own).
    const ownerTotals = {}
    actors.forEach((a) => {
      if (a.isAgent && a.ownerId && a.ownerId !== a.id) ownerTotals[a.ownerId] = (ownerTotals[a.ownerId] || 0) + a.total
    })
    Object.entries(ownerTotals).forEach(([oid, tot]) => {
      const numId = isNaN(+oid) ? oid : +oid
      if (actorIds.has(numId)) return
      const owner = actors.find((a) => a.isAgent && String(a.ownerId) === String(oid))
      add(`o:${oid}`, {
        type: "owner", ownerId: numId, pid: owner ? owner.ownerPid : null,
        label: owner ? owner.ownerLabel : `Owner ${oid}`,
        value: tot, r: 9 + Math.sqrt(tot / maxTotal) * 11, color: OWNER_COLOR
      })
    })

    const edges = []
    actors.forEach((a) => {
      const ai = index.get(`a:${a.id}`)
      for (const [ck, v] of Object.entries(a.counts)) {
        if (v <= 0 || !index.has(`c:${ck}`)) continue
        edges.push({ s: ai, t: index.get(`c:${ck}`), w: v, kind: "activity", color: this.colorById.get(a.id) })
      }
      if (a.isAgent && a.ownerId && a.ownerId !== a.id) {
        const oi = index.has(`a:${a.ownerId}`) ? index.get(`a:${a.ownerId}`) : index.get(`o:${a.ownerId}`)
        if (oi != null) edges.push({ s: ai, t: oi, w: 1, kind: "ownership" })
      }
    })

    return { nodes, edges }
  }

  // Compact Fruchterman-Reingold layout, run to a settled state (no animation).
  simulate(nodes, edges, W, H) {
    const area = W * H
    const k = Math.sqrt(area / Math.max(1, nodes.length)) * 0.62
    nodes.forEach((n, i) => {
      const ang = (i / nodes.length) * Math.PI * 2
      n.x = W / 2 + Math.cos(ang) * (W * 0.22) + (hash(`${n.label}x`) % 20 - 10)
      n.y = H / 2 + Math.sin(ang) * (H * 0.22) + (hash(`${n.label}y`) % 20 - 10)
    })
    let temp = W * 0.10
    for (let it = 0; it < 320; it++) {
      nodes.forEach((a) => { a.dx = 0; a.dy = 0 })
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j]
          let dx = a.x - b.x, dy = a.y - b.y
          let d = Math.hypot(dx, dy) || 0.01
          const rep = (k * k) / d
          const ux = dx / d, uy = dy / d
          a.dx += ux * rep; a.dy += uy * rep
          b.dx -= ux * rep; b.dy -= uy * rep
        }
      }
      for (const e of edges) {
        const a = nodes[e.s], b = nodes[e.t]
        let dx = a.x - b.x, dy = a.y - b.y
        let d = Math.hypot(dx, dy) || 0.01
        const wgt = e.kind === "ownership" ? 1.4 : (0.6 + 0.5 * Math.min(e.w, 6) / 6)
        const att = (d * d) / k * wgt
        const ux = dx / d, uy = dy / d
        a.dx -= ux * att; a.dy -= uy * att
        b.dx += ux * att; b.dy += uy * att
      }
      for (const a of nodes) {
        a.dx += (W / 2 - a.x) * 0.022
        a.dy += (H / 2 - a.y) * 0.022
        const d = Math.hypot(a.dx, a.dy) || 0.01
        a.x += (a.dx / d) * Math.min(d, temp)
        a.y += (a.dy / d) * Math.min(d, temp)
        a.x = Math.max(a.r + 10, Math.min(W - a.r - 10, a.x))
        a.y = Math.max(a.r + 10, Math.min(H - a.r - 10, a.y))
      }
      temp *= 0.985
    }
  }

  topologySvg(actors, cats) {
    const t = this.theme
    const W = 960, H = 470
    const { nodes, edges } = this.buildGraph(actors, cats)
    this.simulate(nodes, edges, W, H)

    // Adjacency for hover focus.
    const adj = nodes.map(() => new Set())
    edges.forEach((e) => { adj[e.s].add(e.t); adj[e.t].add(e.s) })

    const svg = svgEl("svg", { viewBox: `0 0 ${W} ${H}`, width: "100%", role: "img" })
    // height follows the viewBox aspect (no letterboxing); max-width keeps it from
    // ballooning on wide desktops, centered within the card.
    svg.style.cssText = "width:100%;height:auto;max-width:880px;margin:0 auto;overflow:visible;display:block;touch-action:none;"

    // Flat, editorial styling (Bloomberg-like): crisp separators, no glow.
    const sep = t.light ? "#ffffff" : (t.card || "#0f172a")
    const edgeLayer = svgEl("g", {})
    const nodeLayer = svgEl("g", {})
    svg.appendChild(edgeLayer); svg.appendChild(nodeLayer)

    // Edges.
    edges.forEach((e) => {
      const a = nodes[e.s], b = nodes[e.t]
      const ln = svgEl("line", {
        x1: a.x, y1: a.y, x2: b.x, y2: b.y,
        stroke: e.kind === "ownership" ? t.textMuted : e.color,
        "stroke-width": e.kind === "ownership" ? 1 : Math.max(1.25, Math.sqrt(e.w) * 1.15),
        "stroke-linecap": "round",
        opacity: e.kind === "ownership" ? 0.3 : 0.42
      })
      if (e.kind === "ownership") ln.setAttribute("stroke-dasharray", "4 4")
      e.el = ln
      edgeLayer.appendChild(ln)
    })

    const edgesByNode = nodes.map(() => [])
    edges.forEach((e) => { edgesByNode[e.s].push(e); edgesByNode[e.t].push(e) })

    // Nodes (each in a translated group).
    nodes.forEach((n) => {
      const g = svgEl("g", { transform: `translate(${n.x},${n.y})` })
      g.style.cursor = "pointer"
      n.gEl = g

      if (n.type === "category") {
        // Editorial hub: solid severity fill + count in contrasting bold.
        g.appendChild(svgEl("circle", { cx: 0, cy: 0, r: n.r, fill: n.color, stroke: sep, "stroke-width": 2 }))
        const fs = Math.max(12, Math.min(20, n.r * 0.52))
        g.appendChild(svgText(0, fs * 0.35, textOn(n.color), fs, String(n.value), 700))
      } else if (n.type === "owner") {
        g.appendChild(svgEl("circle", { cx: 0, cy: 0, r: n.r, fill: sep, stroke: OWNER_COLOR, "stroke-width": 2.5 }))
        g.appendChild(svgEl("circle", { cx: 0, cy: 0, r: Math.max(2.5, n.r * 0.34), fill: OWNER_COLOR }))
      } else {
        g.appendChild(svgEl("circle", { cx: 0, cy: 0, r: n.r, fill: n.color, stroke: sep, "stroke-width": 1.5 }))
      }

      // Labels: category hubs bold/high-contrast; agents & owners lighter.
      if (n.type === "category") {
        wrapWords(n.label, 18, 2).forEach((ln, i) => {
          g.appendChild(svgText(0, n.r + 15 + i * 12.5, t.textPrimary, 11.5, ln, 700))
        })
      } else {
        g.appendChild(svgText(0, n.r + 13, t.textSecondary, 10.5, truncate(n.label, 22), 600))
      }

      // Interactions.
      this.hoverHandlers(g, () => this.nodeTooltip(n))
      g.addEventListener("mouseenter", () => { if (!this.dragging) this.focus(nodes, edges, n.idx, adj) })
      g.addEventListener("mouseleave", () => { if (!this.dragging) this.focus(nodes, edges, null, adj) })
      g.addEventListener("click", (ev) => {
        if (this.didDrag) { this.didDrag = false; return }
        this.drill(n)
      })
      this.enableDrag(g, n, svg, edgesByNode)

      nodeLayer.appendChild(g)
    })

    // Zoom-to-fit: frame the actual graph so sparse layouts fill the space and
    // dense ones stay readable, instead of floating in a fixed 860x540 canvas.
    this.fitViewBox(svg, nodes)

    const wrap = h("div", "py-2 select-none")
    wrap.appendChild(svg)
    return wrap
  }

  // Tighten the viewBox to the laid-out nodes (plus label extents) with padding,
  // capped to a sane aspect ratio so few-node graphs don't zoom in grotesquely.
  fitViewBox(svg, nodes) {
    if (!nodes.length) return
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    nodes.forEach((n) => {
      const lines = n.type === "category" ? 2 : 1
      const labelBelow = n.r + 6 + lines * 13
      const halfLabel = Math.max(n.r, Math.min(90, String(n.label || "").length * 3.4))
      minX = Math.min(minX, n.x - halfLabel)
      maxX = Math.max(maxX, n.x + halfLabel)
      minY = Math.min(minY, n.y - n.r - 4)
      maxY = Math.max(maxY, n.y + labelBelow)
    })
    const pad = 28
    let x = minX - pad, y = minY - pad
    let w = (maxX - minX) + pad * 2, h = (maxY - minY) + pad * 2
    // Clamp to a landscape aspect (1.9:1 .. 2.4:1) so the panel stays wide and
    // short — keeps the graph from getting tall/oversized on desktop.
    const ar = w / h
    if (ar > 2.4) { const nh = w / 2.4; y -= (nh - h) / 2; h = nh }
    else if (ar < 1.9) { const nw = h * 1.9; x -= (nw - w) / 2; w = nw }
    svg.setAttribute("viewBox", `${x.toFixed(1)} ${y.toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)}`)
  }

  drill(n) {
    if (n.type === "category") window.location.href = this.listUrl(this.catLink(n.key))
    else if (n.pid) window.location.href = this.listUrl({ member_uuid: n.pid })
  }

  // Dim everything outside the hovered node's immediate neighborhood.
  focus(nodes, edges, idx, adj) {
    edges.forEach((e) => {
      const on = idx == null ? false : (e.s === idx || e.t === idx)
      const base = e.kind === "ownership" ? 0.3 : 0.42
      e.el.setAttribute("opacity", idx == null ? base : (on ? 0.95 : 0.05))
    })
    nodes.forEach((n) => {
      const near = idx == null ? true : (n.idx === idx || adj[idx].has(n.idx))
      n.gEl.style.opacity = near ? 1 : 0.12
    })
  }

  enableDrag(g, n, svg, edgesByNode) {
    const toSvg = (ev) => {
      const pt = svg.createSVGPoint()
      pt.x = ev.clientX; pt.y = ev.clientY
      const m = svg.getScreenCTM()
      if (!m) return null
      return pt.matrixTransform(m.inverse())
    }
    let start = null
    const onMove = (ev) => {
      const p = toSvg(ev)
      if (!p) return
      if (start && (Math.abs(p.x - start.x) > 3 || Math.abs(p.y - start.y) > 3)) this.didDrag = true
      n.x = p.x; n.y = p.y
      g.setAttribute("transform", `translate(${n.x},${n.y})`)
      for (const e of edgesByNode[n.idx]) {
        if (e.s === n.idx) { e.el.setAttribute("x1", n.x); e.el.setAttribute("y1", n.y) }
        else { e.el.setAttribute("x2", n.x); e.el.setAttribute("y2", n.y) }
      }
    }
    const onUp = () => {
      this.dragging = false
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
    }
    g.addEventListener("pointerdown", (ev) => {
      ev.preventDefault()
      this.dragging = true
      this.didDrag = false
      start = toSvg(ev)
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
    })
  }

  legendEl(actors) {
    const wrap = h("div", "mt-2 pt-4 border-t border-slate-500/15 flex flex-wrap gap-x-5 gap-y-2")
    actors.slice(0, LEGEND_MAX).forEach((a) => {
      const item = h("div", "flex items-center gap-2 min-w-0 cursor-pointer group")
      const dot = h("span", "inline-block w-2.5 h-2.5 rounded-full flex-shrink-0")
      dot.style.background = this.colorById.get(a.id)
      const label = h("span", "text-xs text-gray-400 truncate max-w-[160px] group-hover:text-white")
      label.textContent = a.label
      label.title = a.label
      const total = h("span", "text-xs font-medium text-gray-300 tabular-nums")
      total.textContent = a.total.toLocaleString()
      item.appendChild(dot); item.appendChild(label); item.appendChild(total)
      const spans = this.span(a)
      if (spans > 1) {
        const badge = h("span", "text-[10px] text-amber-400 tabular-nums")
        badge.textContent = `${spans} cats`
        item.appendChild(badge)
      }
      if (a.pid) item.addEventListener("click", () => { window.location.href = this.listUrl({ member_uuid: a.pid }) })
      wrap.appendChild(item)
    })
    return wrap
  }

  /* ---------- category clusters (pie with leader-line callouts) ---------- */

  pieCard(actors, cats) {
    const card = h("div", "bg-slate-800/30 border border-slate-700 rounded-xl p-4 lg:p-5")
    const header = h("div")
    header.appendChild(hText("h3", "text-sm font-semibold text-white tracking-tight", "Category clusters"))
    header.appendChild(hText("p", "text-xs text-gray-500 mt-0.5",
      "Share of violations by category  \u00b7  hover for contributors  \u00b7  click to drill"))
    card.appendChild(header)

    if (!cats.length) {
      card.appendChild(hText("p", "text-sm text-gray-500 py-10 text-center", "No category data for this range."))
      return card
    }
    card.appendChild(this.pieSvg(actors, cats))
    return card
  }

  pieSvg(actors, cats) {
    const t = this.theme
    const W = 760, H = 380
    const cx = W / 2, cy = H / 2
    const R = 118
    const sep = t.light ? "#ffffff" : (t.card || "#0f172a")
    const leaderStroke = t.light ? "#64748b" : "rgba(148,163,184,0.55)"
    const OTHER = "__other__"
    const OTHER_COLOR = t.light ? "#94a3b8" : "#64748b"

    // Each callout needs vertical room; with too many categories the leader
    // lines stack past the card and collide with sections below. Cap the
    // visible wedges to what fits per side and roll the long tail into a
    // single "Other" slice so the chart stays readable.
    // Worst-case callout height is a two-line label (pct + 2 name lines).
    const rowH = 52
    const perSide = Math.max(3, Math.floor((H - 44) / rowH))
    const maxSlices = Math.min(perSide * 2, 12)
    const sorted = [...cats].sort((a, b) => b.volume - a.volume)
    let display = sorted
    if (sorted.length > maxSlices) {
      const head = sorted.slice(0, maxSlices - 1)
      const tail = sorted.slice(maxSlices - 1)
      const otherVol = tail.reduce((s, c) => s + c.volume, 0)
      display = head.concat([{ key: OTHER, label: `Other (${tail.length} categories)`, volume: otherVol, tail }])
    }

    const total = display.reduce((s, c) => s + c.volume, 0) || 1

    const colorByCat = new Map()
    display.forEach((c, i) => colorByCat.set(c.key, c.key === OTHER ? OTHER_COLOR : PALETTE[i % PALETTE.length]))

    const svg = svgEl("svg", { viewBox: `0 0 ${W} ${H}`, width: "100%", role: "img" })
    svg.style.cssText = "width:100%;height:auto;max-width:780px;margin:0 auto;overflow:visible;display:block;"

    const sliceLayer = svgEl("g", {})
    const leaderLayer = svgEl("g", {})
    const labelLayer = svgEl("g", {})
    svg.appendChild(leaderLayer); svg.appendChild(sliceLayer); svg.appendChild(labelLayer)

    // One slice per category; collect a label anchor on each slice's mid-angle.
    let a = -Math.PI / 2
    const sides = { 1: [], "-1": [] }
    display.forEach((cat) => {
      const isOther = cat.key === OTHER
      const frac = cat.volume / total
      const a0 = a, a1 = a + frac * Math.PI * 2
      a = a1
      const mid = (a0 + a1) / 2
      const x0 = cx + R * Math.cos(a0), y0 = cy + R * Math.sin(a0)
      const x1 = cx + R * Math.cos(a1), y1 = cy + R * Math.sin(a1)
      const large = (a1 - a0) > Math.PI ? 1 : 0
      const path = svgEl("path", {
        d: `M ${cx} ${cy} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`,
        fill: colorByCat.get(cat.key), stroke: sep, "stroke-width": 2, "stroke-linejoin": "round"
      })
      path.style.cssText = isOther ? "transition:opacity .15s;" : "cursor:pointer;transition:opacity .15s;"
      this.hoverHandlers(path, () => isOther ? this.otherSliceTooltip(cat, total) : this.catSliceTooltip(cat, actors, total))
      if (!isOther) path.addEventListener("click", () => { window.location.href = this.listUrl(this.catLink(cat.key)) })
      sliceLayer.appendChild(path)

      const side = Math.cos(mid) >= 0 ? 1 : -1
      sides[side].push({
        cat, side, pct: frac * 100,
        nameLines: wrapWords(cat.label, 22, 2),
        ax: cx + R * Math.cos(mid), ay: cy + R * Math.sin(mid),
        y: cy + R * Math.sin(mid)
      })
    })

    // Spread each side's labels vertically so they never overlap. Spacing
    // adapts to each label's line count so a wrapped (two-line) label can't be
    // overrun by the next callout's percentage.
    ;[1, -1].forEach((s) => this.spreadLabels(sides[s], 22, H - 22))

    const bend = R + 22
    const lineEnd = R + 76
    ;[1, -1].forEach((s) => {
      sides[s].forEach((it) => {
        const bx = cx + s * bend
        const ex = cx + s * lineEnd
        leaderLayer.appendChild(svgEl("polyline", {
          points: `${it.ax.toFixed(1)},${it.ay.toFixed(1)} ${bx.toFixed(1)},${it.y.toFixed(1)} ${ex.toFixed(1)},${it.y.toFixed(1)}`,
          fill: "none", stroke: leaderStroke, "stroke-width": 1.25
        }))

        const tx = ex + s * 7
        const anchor = s === 1 ? "start" : "end"
        const pct = svgEl("text", {
          x: tx, y: it.y - 3, "text-anchor": anchor, "font-size": 17, "font-weight": 700, fill: t.textPrimary
        })
        pct.textContent = it.pct < 1 ? "<1%" : `${Math.round(it.pct)}%`
        labelLayer.appendChild(pct)

        it.nameLines.forEach((ln, i) => {
          const nm = svgEl("text", {
            x: tx, y: it.y + 13 + i * 13, "text-anchor": anchor, "font-size": 11.5, fill: t.textSecondary
          })
          nm.textContent = ln
          labelLayer.appendChild(nm)
        })
      })
    })

    const wrap = h("div", "py-4 select-none")
    wrap.appendChild(svg)
    return wrap
  }

  // Push a column of label anchors apart vertically (min `gap`) and keep them
  // within [top, bottom], so leader-line callouts never collide.
  spreadLabels(items, top, bottom) {
    if (!items.length) return
    items.sort((p, q) => p.y - q.y)
    // Minimum center-to-center gap between an upper label and the one below it:
    // the upper label's downward extent (name lines) + the lower label's
    // percentage height + padding. This keeps the next "%" clear of a wrapped
    // two-line label above it.
    const PCT_UP = 18, LINE = 13, PAD = 7
    const sep = (upper) => LINE + ((upper.nameLines?.length || 1) - 1) * LINE + PCT_UP + PAD
    const gaps = items.map((_, i) => (i === 0 ? 0 : sep(items[i - 1])))

    for (let i = 1; i < items.length; i++) {
      if (items[i].y < items[i - 1].y + gaps[i]) items[i].y = items[i - 1].y + gaps[i]
    }
    if (items[items.length - 1].y > bottom) {
      items[items.length - 1].y = bottom
      for (let i = items.length - 2; i >= 0; i--) {
        if (items[i].y > items[i + 1].y - gaps[i + 1]) items[i].y = items[i + 1].y - gaps[i + 1]
      }
    }
    if (items[0].y < top) {
      items[0].y = top
      for (let i = 1; i < items.length; i++) {
        if (items[i].y < items[i - 1].y + gaps[i]) items[i].y = items[i - 1].y + gaps[i]
      }
    }
  }

  catSliceTooltip(cat, actors, total) {
    const t = this.theme
    const pct = total ? Math.round((cat.volume / total) * 100) : 0
    const rows = this.contributors(cat.key, actors).slice(0, 3).map((c) =>
      `<div style="display:flex;align-items:center;gap:6px;margin-top:3px;">` +
      `<span style="flex:1 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(c.label)}</span>` +
      `<span style="font-weight:600;">${c.count}</span></div>`).join("")
    return `<div style="font-weight:600;margin-bottom:1px;">${esc(cat.label)}</div>` +
      `<div style="color:${t.textMuted};font-size:11px;">${cat.volume} violations \u00b7 ${pct}% of total</div>` +
      (rows ? `<div style="margin-top:4px;">${rows}</div>` : "")
  }

  otherSliceTooltip(cat, total) {
    const t = this.theme
    const pct = total ? Math.round((cat.volume / total) * 100) : 0
    const tail = (cat.tail || []).slice().sort((a, b) => b.volume - a.volume)
    const rows = tail.slice(0, 6).map((c) =>
      `<div style="display:flex;align-items:center;gap:6px;margin-top:3px;">` +
      `<span style="flex:1 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(c.label)}</span>` +
      `<span style="font-weight:600;">${c.volume}</span></div>`).join("")
    const more = tail.length > 6 ? `<div style="color:${t.textMuted};font-size:11px;margin-top:3px;">+ ${tail.length - 6} more</div>` : ""
    return `<div style="font-weight:600;margin-bottom:1px;">${esc(cat.label)}</div>` +
      `<div style="color:${t.textMuted};font-size:11px;">${cat.volume} violations \u00b7 ${pct}% of total</div>` +
      (rows ? `<div style="margin-top:4px;">${rows}</div>` : "") + more
  }

  /* ------------------------------------------------------------------ */
  /* Tooltip                                                             */
  /* ------------------------------------------------------------------ */

  buildTooltip() {
    const t = this.theme
    const tip = h("div")
    tip.style.cssText = `position:absolute;display:none;pointer-events:none;z-index:50;max-width:300px;` +
      `background:${t.card};border:1px solid ${t.border};border-radius:8px;padding:8px 10px;` +
      `color:${t.textPrimary};font-size:12px;line-height:1.35;box-shadow:0 6px 20px rgba(0,0,0,0.28);`
    this.tooltipEl = tip
    return tip
  }

  nodeTooltip(n) {
    if (n.type === "category") return this.categoryTooltip(n)
    return this.actorTooltip(n)
  }

  categoryTooltip(n) {
    const t = this.theme
    const mix = (this.payloadValue.category_severity || {})[n.key] || {}
    const open = (this.payloadValue.category_open || {})[n.key] || 0
    const sevRows = Object.keys(SEV)
      .sort((a, b) => SEV[b].rank - SEV[a].rank)
      .filter((s) => mix[s])
      .map((s) => `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:8px;">` +
        `<span style="width:8px;height:8px;border-radius:9999px;background:${SEV[s].color};"></span>` +
        `${SEV[s].label} ${mix[s]}</span>`).join("")
    return `<div style="font-weight:600;margin-bottom:1px;">${esc(n.label)}</div>` +
      `<div style="color:${t.textMuted};font-size:11px;">${n.value} violations · ${open} open</div>` +
      `<div style="margin-top:5px;display:flex;flex-wrap:wrap;">${sevRows}</div>`
  }

  actorTooltip(n) {
    const t = this.theme
    const labels = this.payloadValue.category_labels || {}
    const role = n.type === "owner" ? "Owner" : (n.type === "agent" ? "Agent" : "User")
    const counts = n.actor ? n.actor.counts : {}
    const entries = Object.entries(counts).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1])
    const rows = entries.map(([k, v]) =>
      `<div style="display:flex;align-items:center;gap:6px;margin-top:3px;">` +
      `<span style="flex:1 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(labels[k] || k)}</span>` +
      `<span style="font-weight:600;">${v}</span></div>`).join("")
    let body = ""
    if (n.type === "owner") body = `<div style="color:${t.textMuted};font-size:11px;">Owns agents · ${n.value} violations across owned agents</div>`
    else body = `<div style="color:${t.textMuted};font-size:11px;">${entries.length} categories · ${n.value} total</div><div style="margin-top:4px;">${rows}</div>`
    return `<div style="display:flex;align-items:center;gap:6px;margin-bottom:1px;">` +
      `<span style="width:9px;height:9px;border-radius:9999px;background:${n.color};flex:0 0 auto;"></span>` +
      `<span style="font-weight:600;">${esc(n.label)}</span>` +
      `<span style="color:${t.textMuted};font-size:10px;text-transform:uppercase;letter-spacing:.04em;">${role}</span></div>${body}`
  }

  showTooltip(html, e) {
    const tip = this.tooltipEl
    if (!tip) return
    tip.innerHTML = html
    tip.style.display = "block"
    this.moveTooltip(e)
  }

  moveTooltip(e) {
    const tip = this.tooltipEl
    if (!tip || tip.style.display === "none") return
    const rect = this.rootTarget.getBoundingClientRect()
    let x = e.clientX - rect.left + 14
    let y = e.clientY - rect.top + 14
    const tw = tip.offsetWidth || 240
    if (x + tw > rect.width) x = Math.max(4, rect.width - tw - 4)
    tip.style.left = `${x}px`
    tip.style.top = `${y}px`
  }

  hideTooltip() {
    if (this.tooltipEl) this.tooltipEl.style.display = "none"
  }

  hoverHandlers(el, htmlFn) {
    el.addEventListener("mouseenter", (e) => this.showTooltip(htmlFn(), e))
    el.addEventListener("mousemove", (e) => this.moveTooltip(e))
    el.addEventListener("mouseleave", () => this.hideTooltip())
  }

  /* ------------------------------------------------------------------ */
  /* Links                                                               */
  /* ------------------------------------------------------------------ */

  catLink(key) {
    return (this.payloadValue.category_links || {})[key] || {}
  }

  listUrl(extra = {}) {
    const f = Object.assign({}, this.filtersValue, { violation_type: "", verifier_id: "" }, extra, { view: "", page: "" })
    const qs = Object.entries(f)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&")
    return `${this.basePathValue}?${qs}`
  }
}

/* -------------------------------------------------------------------- */
/* helpers                                                              */
/* -------------------------------------------------------------------- */

function h(tag, cls) {
  const e = document.createElement(tag)
  if (cls) e.className = cls
  return e
}
function hText(tag, cls, text) {
  const e = h(tag, cls); e.textContent = text; return e
}
function svgEl(tag, attrs) {
  const e = document.createElementNS(NS, tag)
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v)
  return e
}
function svgText(x, y, fill, size, text, weight) {
  const t = svgEl("text", { x, y, "text-anchor": "middle", "font-size": size, fill, "font-weight": weight || 400 })
  t.textContent = text
  return t
}
function hash(s) {
  let v = 0
  const str = String(s)
  for (let i = 0; i < str.length; i++) v = (v * 31 + str.charCodeAt(i)) | 0
  return Math.abs(v)
}
function truncate(s, n) {
  const str = String(s)
  return str.length > n ? `${str.slice(0, n - 1)}\u2026` : str
}
function wrapWords(text, maxChars, maxLines) {
  const words = String(text).split(/\s+/).filter(Boolean)
  if (words.length === 0) return [String(text)]
  const lines = []
  let cur = ""
  for (const w of words) {
    const cand = cur ? `${cur} ${w}` : w
    if (cand.length <= maxChars || !cur) cur = cand
    else { lines.push(cur); cur = w; if (lines.length === maxLines) break }
  }
  if (lines.length < maxLines && cur) lines.push(cur)
  const used = lines.join(" ").replace(/\s+/g, " ").trim()
  const full = String(text).replace(/\s+/g, " ").trim()
  if (used.length < full.length) {
    const i = lines.length - 1
    lines[i] = `${lines[i].slice(0, Math.max(0, maxChars - 1))}\u2026`
  }
  return lines
}
function esc(s) {
  return String(s).replace(/[&<>"']/g, (ch) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]
  ))
}
// Pick black or white text for legibility on a solid hex fill.
function textOn(hex) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex))
  if (!m) return "#0f172a"
  const v = parseInt(m[1], 16)
  const lum = 0.299 * ((v >> 16) & 255) + 0.587 * ((v >> 8) & 255) + 0.114 * (v & 255)
  return lum > 150 ? "#0f172a" : "#ffffff"
}
